import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { storableError } from '../../util/errors';
import { subscriptionsAPI, listingsAPI } from '../../api';
import { fetchCurrentUser } from '../../ducks/user.duck';

// ================ Action types ================ //

export const FETCH_LISTINGS_REQUEST = 'app/ManageSubscriptionsPage/FETCH_LISTINGS_REQUEST';
export const FETCH_LISTINGS_SUCCESS = 'app/ManageSubscriptionsPage/FETCH_LISTINGS_SUCCESS';
export const FETCH_LISTINGS_ERROR = 'app/ManageSubscriptionsPage/FETCH_LISTINGS_ERROR';

export const FETCH_SUBSCRIPTIONS_REQUEST =
  'app/ManageSubscriptionsPage/FETCH_SUBSCRIPTIONS_REQUEST';
export const FETCH_SUBSCRIPTIONS_SUCCESS =
  'app/ManageSubscriptionsPage/FETCH_SUBSCRIPTIONS_SUCCESS';
export const FETCH_SUBSCRIPTIONS_ERROR = 'app/ManageSubscriptionsPage/FETCH_SUBSCRIPTIONS_ERROR';

export const UNSUBSCRIBE_REQUEST = 'app/ManageSubscriptionsPage/UNSUBSCRIBE_REQUEST';
export const UNSUBSCRIBE_SUCCESS = 'app/ManageSubscriptionsPage/UNSUBSCRIBE_SUCCESS';
export const UNSUBSCRIBE_ERROR = 'app/ManageSubscriptionsPage/UNSUBSCRIBE_ERROR';

// ================ Reducer ================ //

const initialState = {
  fetchListingsInProgress: false,
  fetchListingsError: null,
  listingIds: [],
  subscriptionsAreLoaded: false,
  fetchSubscriptionsInProgress: false,
  fetchSubscriptionsError: null,
  subscriptions: [],
  unsubscribeInProgress: false,
  unsubscribeError: null,
};

const resultIds = data => data.data.map(l => l.id);

const manageSubscriptionsPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case FETCH_LISTINGS_REQUEST:
      return {
        ...state,
        fetchListingsInProgress: true,
        fetchListingsError: null,
      };
    case FETCH_LISTINGS_SUCCESS:
      return {
        ...state,
        fetchListingsInProgress: false,
        fetchListingsError: null,
        listingIds: resultIds(payload.data),
      };
    case FETCH_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        fetchListingsInProgress: false,
        fetchListingsError: payload,
      };

    case FETCH_SUBSCRIPTIONS_REQUEST:
      return {
        ...state,
        subscriptionsAreLoaded: false,
        fetchSubscriptionsInProgress: true,
        fetchSubscriptionsError: null,
      };
    case FETCH_SUBSCRIPTIONS_SUCCESS:
      return {
        ...state,
        subscriptionsAreLoaded: true,
        fetchSubscriptionsInProgress: false,
        fetchSubscriptionsError: null,
        subscriptions: payload.subscriptions,
      };
    case FETCH_SUBSCRIPTIONS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        subscriptionsAreLoaded: false,
        fetchSubscriptionsInProgress: false,
        fetchSubscriptionsError: payload,
      };

    case UNSUBSCRIBE_REQUEST:
      return {
        ...state,
        unsubscribeInProgress: true,
        unsubscribeError: null,
      };
    case UNSUBSCRIBE_SUCCESS:
      return {
        ...state,
        unsubscribeInProgress: false,
        unsubscribeError: null,
        // Remove the unsubscribed listing from subscriptions
        subscriptions: state.subscriptions.filter(
          subscription => subscription.id !== payload.subscriptionId
        ),
      };
    case UNSUBSCRIBE_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        unsubscribeInProgress: false,
        unsubscribeError: payload,
      };

    default:
      return state;
  }
};

export default manageSubscriptionsPageReducer;

// ================ Action creators ================ //

export const fetchListingsRequest = queryParams => ({
  type: FETCH_LISTINGS_REQUEST,
  payload: { queryParams },
});

export const fetchListingsSuccess = response => ({
  type: FETCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const fetchListingsError = e => ({
  type: FETCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const fetchSubscriptionsRequest = () => ({
  type: FETCH_SUBSCRIPTIONS_REQUEST,
});

export const fetchSubscriptionsSuccess = subscriptions => ({
  type: FETCH_SUBSCRIPTIONS_SUCCESS,
  payload: { subscriptions },
});

export const fetchSubscriptionsError = e => ({
  type: FETCH_SUBSCRIPTIONS_ERROR,
  error: true,
  payload: e,
});

export const unsubscribeRequest = () => ({
  type: UNSUBSCRIBE_REQUEST,
});

export const unsubscribeSuccess = subscriptionId => ({
  type: UNSUBSCRIBE_SUCCESS,
  payload: { subscriptionId },
});

export const unsubscribeError = e => ({
  type: UNSUBSCRIBE_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const fetchListings = subscriptions => (dispatch, getState, sdk) => {
  const subscriptionListingIds = subscriptions.map(m => m.listingId);
  const params = {
    ids: subscriptionListingIds,
  };

  dispatch(fetchListingsRequest(params));

  return sdk.listings
    .query({
      ...params,
      include: ['images', 'author'],
      'fields.image': ['variants.square-small', 'variants.square-medium'],
      'limit.images': 1,
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(fetchSubscriptionsSuccess(subscriptions));
      dispatch(fetchListingsSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(fetchListingsError(storableError(e)));
      throw e;
    });
};

export const fetchSubscriptions = () => (dispatch, getState, sdk) => {
  dispatch(fetchSubscriptionsRequest());

  return sdk.currentUser
    .show()
    .then(response => {
      const currentUser = response.data.data;
      const subscriptions = currentUser.attributes.profile.metadata.memberships || [];
      return subscriptions;
    })
    .catch(e => dispatch(fetchSubscriptionsError(storableError(e))));
};

export const unsubscribe = params => (dispatch, getState, sdk) => {
  dispatch(unsubscribeRequest());

  const { listingId, subscriptionId, userId } = params;

  return subscriptionsAPI
    .deleteSubscription({ listingId, subscriptionId, userId })
    .then(response => {
      dispatch(unsubscribeSuccess(subscriptionId));
      dispatch(fetchCurrentUser());
      return response;
    })
    .catch(e => {
      dispatch(unsubscribeError(storableError(e)));
      // Rethrow so the confirmation modal stays open and says it failed,
      // instead of closing as if the member had left.
      throw e;
    });
};

export const loadData = () => (dispatch, getState, sdk) => {
  return dispatch(fetchSubscriptions()).then(subscriptions =>
    dispatch(fetchListings(subscriptions))
  );
};
